import { ReactElement, useState } from 'react';

import { type Specialty, useDentistsContext } from '..';
import { RefreshIcon } from '../../icons/RefreshIcon';

type AnswerKey =
  | 'Urgencia 24h'
  | 'Clinico Geral'
  | 'Dentistica'
  | 'Cirurgia'
  | 'Periodontia'
  | 'Ortodontia'
  | 'Protese'
  | 'Endodontia';
type Question = {
  question: string;
  helper?: string;
  yes: QuestionObj;
  no: QuestionObj;
};
type Answer = { answer: AnswerKey; helper?: string };
type QuestionObj = Question | Answer;
type HistoryEntry = { question: Question; answer: 'yes' | 'no' };

const specialties: Record<AnswerKey, Specialty> = {
  'Urgencia 24h': {
    idEspecialidade: 12,
    nomeEspecialidade: 'URGENCIA DIURNA',
    quantidade: 1585,
  },
  'Clinico Geral': {
    idEspecialidade: 44,
    nomeEspecialidade: 'CLINICO GERAL',
    quantidade: 1585,
  },
  Dentistica: {
    idEspecialidade: 4,
    nomeEspecialidade: 'DENTISTICA',
    quantidade: 1415,
  },
  Cirurgia: {
    idEspecialidade: 8,
    nomeEspecialidade: 'CIRURGIA',
    quantidade: 957,
  },
  Periodontia: {
    idEspecialidade: 6,
    nomeEspecialidade: 'PERIODONTIA',
    quantidade: 1302,
  },
  Ortodontia: {
    idEspecialidade: 9,
    nomeEspecialidade: 'ORTODONTIA',
    quantidade: 829,
  },
  Protese: {
    idEspecialidade: 7,
    nomeEspecialidade: 'PROTESE',
    quantidade: 241,
  },
  Endodontia: {
    idEspecialidade: 5,
    nomeEspecialidade: 'ENDODONTIA',
    quantidade: 938,
  },
};

const questionObj: Question = {
  question: 'Esta com dor forte ou latejante?',
  helper: 'Responda para encontrarmos uma especialidade provavel.',
  yes: {
    question: 'Ha sangramento ou trauma na boca?',
    helper: 'Sangramento ou trauma costuma indicar atendimento de urgencia.',
    yes: { answer: 'Urgencia 24h' },
    no: { answer: 'Clinico Geral' },
  },
  no: {
    question: 'Alguma restauracao caiu ou um dente quebrou?',
    yes: { answer: 'Dentistica' },
    no: {
      question: 'Precisa extrair algum dente?',
      yes: { answer: 'Cirurgia' },
      no: {
        question: 'Precisa fazer limpeza ou tratar gengiva?',
        yes: { answer: 'Periodontia' },
        no: {
          question: 'Precisa colocar ou ajustar aparelho?',
          yes: { answer: 'Ortodontia' },
          no: {
            question: 'Precisa repor um dente perdido?',
            yes: { answer: 'Protese' },
            no: {
              question: 'Precisa realizar tratamento de canal?',
              yes: { answer: 'Endodontia' },
              no: { answer: 'Clinico Geral' },
            },
          },
        },
      },
    },
  },
};

const getAnswerLabel = (answer: 'yes' | 'no') =>
  answer === 'yes' ? 'Sim' : 'Nao';

export default function QuestionBox(): ReactElement {
  const [current, setCurrent] = useState<Question>(questionObj);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [result, setResult] = useState<AnswerKey | null>(null);
  const { setSpecialty } = useDentistsContext();

  function answer(resp: 'yes' | 'no') {
    const newQuestion = current[resp];
    const nextHistory = [...history, { question: current, answer: resp }];

    setHistory(nextHistory);

    if ((newQuestion as Answer).answer) {
      const ans = newQuestion as Answer;
      setResult(ans.answer);
      setSpecialty(specialties[ans.answer]);
      return;
    }

    setCurrent(newQuestion as Question);
  }

  function back() {
    const previousQuestion = history[history.length - 1];
    if (!previousQuestion) return;

    setCurrent(previousQuestion.question);
    setHistory(history.slice(0, -1));
    setResult(null);
    setSpecialty(null as any);
  }

  function editAnswer(index: number) {
    const selected = history[index];
    if (!selected) return;

    setCurrent(selected.question);
    setHistory(history.slice(0, index));
    setResult(null);
    setSpecialty(null as any);
  }

  function reset() {
    setCurrent(questionObj);
    setHistory([]);
    setResult(null);
    setSpecialty(null as any);
  }

  const currentStep = result ? history.length : history.length + 1;

  return (
    <div className="w-full rounded-lg border border-neutral-lighter bg-neutral-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase text-primary-main">
            Guia de especialidade
          </p>
          <p className="mt-1 text-sm text-medium-emphasis">
            Use as respostas para receber uma sugest&atilde;o. Voce pode voltar
            ou clicar em uma etapa para alterar.
          </p>
        </div>
        <span className="rounded-full bg-primary-container px-3 py-1 text-xs font-bold uppercase text-primary-main">
          {result ? 'Sugestao pronta' : 'Pergunta ' + currentStep}
        </span>
      </div>

      {history.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-neutral-lightest pt-3">
          {history.map((item, index) => (
            <button
              key={index}
              title={item.question.question}
              onClick={() => editAnswer(index)}
              className="rounded-full border border-primary-container bg-primary-container px-3 py-1 text-xs font-semibold text-primary-main transition hover:border-primary-main hover:bg-neutral-white"
            >
              {'#' + (index + 1) + ' ' + getAnswerLabel(item.answer)}
            </button>
          ))}
        </div>
      )}

      {result ? (
        <div className="mt-4 rounded-lg bg-primary-container2 p-4">
          <p className="text-sm font-semibold uppercase text-primary-main">
            Especialidade sugerida
          </p>
          <h4 className="mt-1 text-xl font-bold text-high-emphasis">
            {result}
          </h4>
          <p className="mt-2 text-sm text-medium-emphasis">
            A busca ja foi preparada com essa especialidade. Se quiser revisar,
            volte uma resposta ou refaca o guia.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={back}
              className="rounded-lg border border-primary-main px-4 py-2 text-sm font-bold uppercase text-primary-main transition hover:bg-primary-container"
            >
              Voltar resposta
            </button>
            <button
              onClick={reset}
              className="flex items-center gap-2 rounded-lg bg-primary-main px-4 py-2 text-sm font-bold uppercase text-white transition hover:bg-primary-dark"
            >
              <RefreshIcon type="light" />
              Refazer
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-lg bg-neutral-surface p-4">
          <p className="text-lg font-semibold text-high-emphasis">
            {current.question}
          </p>
          {current.helper && (
            <p className="mt-1 text-sm text-medium-emphasis">
              {current.helper}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <button
              disabled={history.length === 0}
              onClick={back}
              className="rounded-lg border border-neutral-lighter px-4 py-3 text-sm font-bold uppercase text-medium-emphasis transition hover:border-primary-main hover:text-primary-main disabled:cursor-default disabled:opacity-50 disabled:hover:border-neutral-lighter disabled:hover:text-medium-emphasis"
            >
              Voltar
            </button>

            <div className="flex flex-1 justify-end gap-3 max-[720px]:w-full max-[720px]:justify-stretch">
              <button
                onClick={() => answer('no')}
                className="flex min-h-[48px] min-w-[110px] items-center justify-center gap-2 rounded-lg bg-destructive-container2 px-5 py-3 text-sm font-bold uppercase text-destructive-dark transition hover:-translate-y-0.5 hover:shadow-card max-[720px]:flex-1"
              >
                <img src="/assets/images/icons/x-circle.svg" alt="" />
                N&atilde;o
              </button>
              <button
                onClick={() => answer('yes')}
                className="flex min-h-[48px] min-w-[110px] items-center justify-center gap-2 rounded-lg bg-success-container px-5 py-3 text-sm font-bold uppercase text-success-dark transition hover:-translate-y-0.5 hover:shadow-card max-[720px]:flex-1"
              >
                <img src="/assets/images/icons/check-circle.svg" alt="" />
                Sim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
